'use client'

/**
 * Search & Remediate v2 data hooks (contracts/snr-api.md, all under /v1/er/search-remediate).
 *
 * The browser never compiles a filter and never counts: POST /compile returns what will run,
 * GET /:id/progress and /actions/:jobId/progress return SQL aggregates, and every list is
 * sorted, filtered and paged by the server. Every call goes through `erFetch` (the bearer
 * token, `x-workspace-id` and the ER domain header attach themselves).
 *
 * LIVE PROGRESS. `useSnrRunProgress` and `useSnrActionProgress` poll every 2 seconds while the
 * run or the job is working and stop when it is over. They are the ONLY places that poll, and
 * the components that show progress (components/er/search-remediate/snr-live-progress.tsx) are
 * the only ones that draw it: the platform's floating activity dock replaces this seam in
 * round B without touching the page around it.
 *
 * THE JOBS OF A RUN come from the server (`GET /:id/jobs`, newest first): nothing about a job is
 * remembered in this browser, so a reload, another browser and the activity dock all show the same list.
 *
 * A run made before v2 answers `GET /:id/progress` in its old shape (`totalExecutions`, ...).
 * `useSnrRunProgress` tells the two apart and hands the page `{ kind: 'legacy' | 'v2', ... }`,
 * so the page opens a legacy run in the legacy drawer and a v2 run in the v2 run view.
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { erKeys } from '@/lib/er/query-keys'
import { erDownload, erFetch, erQueryString, saveBlob, type ErListMeta, selectEnvelopeData, selectEnvelopeMeta } from '@/lib/er/api'
import { useWorkspaceScopedEnabled } from '@/lib/api/use-workspace-scoped-enabled'
import type { ErListEnvelope, SnrProgress, SnrSearch } from '@/lib/er/types'
import { notifyActivitiesChanged } from '@/lib/activities/events'
import {
  isSnrActionActive,
  isSnrActionOpen,
  isSnrRunActive,
  type ErSnrQuery,
  type SnrActionJobStatus,
  type SnrActionPreview,
  type SnrActionProgress,
  type SnrActionVerb,
  type SnrCompileResult,
  type SnrDeleteGate,
  type SnrFacets,
  type SnrJobRow,
  type SnrMailboxRow,
  type SnrMailboxStatus,
  type SnrMessageHeaders,
  type SnrMessageRow,
  type SnrResultFilters,
  type SnrRunListExtras,
  type SnrRunProgress,
  type SnrSavedSearch,
  type SnrScope,
  type SnrSelection,
  type SnrUndoPreview,
} from '@/lib/er/snr-query/types'

/** The 2-second live-progress cadence (design 4: "SSE with a 2 s poll fallback"). */
export const SNR_POLL_MS = 2000

const base = (path = '') => `search-remediate${path ? `/${path}` : ''}`
const post = (body?: unknown): RequestInit => ({
  method: 'POST',
  ...(body === undefined ? {} : { body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
})
const put = (body: unknown): RequestInit => ({
  method: 'PUT',
  body: JSON.stringify(body),
  headers: { 'Content-Type': 'application/json' },
})
const patch = (body: unknown): RequestInit => ({
  method: 'PATCH',
  body: JSON.stringify(body),
  headers: { 'Content-Type': 'application/json' },
})

// ── Keys ────────────────────────────────────────────────────────────────────────

/** Under the S&R root, so every existing invalidation of `erKeys.searchRemediate.all()` reaches them. */
export const snrV2Keys = {
  all: () => [...erKeys.searchRemediate.all(), 'v2'] as const,
  compile: (fingerprint: string) => [...snrV2Keys.all(), 'compile', fingerprint] as const,
  progress: (id: string) => [...snrV2Keys.all(), 'progress', id] as const,
  messages: (id: string, params: unknown) => [...snrV2Keys.all(), 'messages', id, params] as const,
  messagesOf: (id: string) => [...snrV2Keys.all(), 'messages', id] as const,
  mailboxes: (id: string, params: unknown) => [...snrV2Keys.all(), 'mailboxes', id, params] as const,
  mailboxesOf: (id: string) => [...snrV2Keys.all(), 'mailboxes', id] as const,
  facets: (id: string, params: unknown) => [...snrV2Keys.all(), 'facets', id, params] as const,
  action: (jobId: string) => [...snrV2Keys.all(), 'action', jobId] as const,
  jobsOf: (runId: string) => [...snrV2Keys.all(), 'jobs', runId] as const,
  headers: (runId: string, messageId: string) => [...snrV2Keys.all(), 'headers', runId, messageId] as const,
  suppressed: () => [...snrV2Keys.all(), 'suppressed'] as const,
  settings: () => [...snrV2Keys.all(), 'settings'] as const,
  saved: (scope: string) => [...snrV2Keys.all(), 'saved', scope] as const,
}

// ── Compile ─────────────────────────────────────────────────────────────────────

/**
 * "What will run": POST /compile. The caller debounces the inputs and passes `enabled` false
 * while there is nothing finished to compile. The previous answer stays on screen while the
 * next one loads, so the panel does not flash between keystrokes.
 */
export function useSnrCompile(args: {
  query: ErSnrQuery | null
  scope: SnrScope
  fingerprint: string
  enabled?: boolean
}) {
  const scoped = useWorkspaceScopedEnabled()
  const { query, scope, fingerprint, enabled = true } = args
  return useQuery({
    queryKey: snrV2Keys.compile(fingerprint),
    queryFn: () => erFetch<SnrCompileResult>(base('compile'), post({ query, scope })),
    enabled: scoped && enabled && query !== null,
    placeholderData: keepPreviousData,
    staleTime: 0,
    gcTime: 30_000,
    retry: false,
  })
}

// ── Ask AI ──────────────────────────────────────────────────────────────────────

/** What POST /ai/parse answers (task 4.7): the filter, in words, with the compile of that filter. */
export interface SnrAiParseResult {
  /** The same ErSnrQuery the builder makes, validated by the server. */
  query: ErSnrQuery
  /** One line, in the language of the sentence. */
  explanation: string
  /** The choices the model made where the request was ambiguous. */
  assumptions: string[]
  /** The parts of the request a filter cannot express ("looks urgent", an action, a mailbox). */
  unsupported: string[]
  compile: SnrCompileResult
}

export interface SnrAiParseArgs {
  sentence: string
  /** Where the analyst is searching: only the providers present there matter to the compile. */
  scope: SnrScope
  /** The interface language (en, ar, hi, fr), a hint for the explanation. */
  locale: string
}

/**
 * Ask AI: a sentence becomes a filter. It starts nothing and changes nothing on the server, so it invalidates
 * no query: the page decides what to do with the draft. A refusal (422 er_snr_ai_unparsed, 400, 429, 503)
 * reaches the caller with its errorCode; `snrAiRefusal` (lib/er/snr-refusal.ts) words it.
 */
export function useSnrAiParse() {
  return useMutation({
    mutationFn: ({ sentence, scope, locale }: SnrAiParseArgs) =>
      erFetch<SnrAiParseResult>(base('ai/parse'), post({ sentence, scope, locale })),
  })
}

// ── Start: dry run and run ──────────────────────────────────────────────────────

export interface SnrStartArgs {
  query: ErSnrQuery
  scope: SnrScope
  planHash: string
  name?: string
}

export function useSnrDryRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ query, scope, planHash }: SnrStartArgs) =>
      erFetch<{ id: string; mode: 'dry_run'; status: string }>(base('dry-runs'), post({ query, scope, planHash })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: erKeys.searchRemediate.all() }),
  })
}

export function useSnrStartRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ query, scope, planHash, name }: SnrStartArgs) =>
      erFetch<{ id: string; status: string }>(base('runs'), post({ query, scope, planHash, ...(name ? { name } : {}) })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: erKeys.searchRemediate.all() }),
  })
}

// ── Progress (the only pollers) ─────────────────────────────────────────────────

export type SnrRunProgressResult =
  | { kind: 'v2'; progress: SnrRunProgress }
  | { kind: 'legacy'; progress: SnrProgress }

/** A v2 progress carries `mailboxes` as an object; a legacy one carries `totalExecutions`. */
export function classifyRunProgress(raw: unknown): SnrRunProgressResult {
  const rec = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const isV2 = !!rec.mailboxes && typeof rec.mailboxes === 'object' && !('totalExecutions' in rec)
  return isV2
    ? { kind: 'v2', progress: raw as SnrRunProgress }
    : { kind: 'legacy', progress: raw as SnrProgress }
}

/**
 * The lists of a run poll only WHILE it works, so their last read can be one taken before the final matches or moves
 * were stored (release-2 QA, 28 Oct 2026: "This search found nothing" under "2 messages found", and a row still
 * "Quarantine · Running" after its job was Done). So when a search or a job stops working, its results are read once
 * more. It is decided in the progress queryFn, which runs once per read however many screens watch the run.
 * A job's progress names no run: every mounted results list is read again (only the open run's are mounted).
 */
function refreshRunResults(qc: QueryClient, runId: string | null) {
  const of = (part: string) => (runId ? [...snrV2Keys.all(), part, runId] : [...snrV2Keys.all(), part])
  void qc.invalidateQueries({ queryKey: of('messages') })
  void qc.invalidateQueries({ queryKey: of('mailboxes') })
  void qc.invalidateQueries({ queryKey: of('facets') })
}

const runWorking = (p: SnrRunProgressResult | undefined) =>
  !!p && p.kind === 'v2' && (p.progress.status === 'Pending' || p.progress.status === 'Searching')

export function useSnrRunProgress(id: string | null) {
  const scoped = useWorkspaceScopedEnabled()
  const qc = useQueryClient()
  return useQuery({
    queryKey: id ? snrV2Keys.progress(id) : [...snrV2Keys.all(), 'progress', 'idle'],
    queryFn: async () => {
      const next = classifyRunProgress(await erFetch<unknown>(base(`${id}/progress`)))
      if (id && runWorking(qc.getQueryData<SnrRunProgressResult>(snrV2Keys.progress(id))) && !runWorking(next)) {
        refreshRunResults(qc, id)
      }
      return next
    },
    enabled: scoped && !!id,
    refetchInterval: (query) => {
      const data = query.state.data as SnrRunProgressResult | undefined
      if (!data) return false
      // A legacy run is not polled here: its own drawer owns that.
      return data.kind === 'v2' && isSnrRunActive(data.progress.status) ? SNR_POLL_MS : false
    },
    refetchIntervalInBackground: false,
  })
}

export function useSnrActionProgress(jobId: string | null) {
  const scoped = useWorkspaceScopedEnabled()
  const qc = useQueryClient()
  return useQuery({
    queryKey: jobId ? snrV2Keys.action(jobId) : [...snrV2Keys.all(), 'action', 'idle'],
    queryFn: async () => {
      const next = await erFetch<SnrActionProgress>(base(`actions/${jobId}/progress`))
      const prev = jobId ? qc.getQueryData<SnrActionProgress>(snrV2Keys.action(jobId)) : undefined
      if (prev && isSnrActionActive(prev.status) && !isSnrActionActive(next.status)) refreshRunResults(qc, null)
      return next
    },
    enabled: scoped && !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data as SnrActionProgress | undefined
      // A paused job is polled too: a Resume from the activity dock (or another tab) shows here within two seconds.
      return data && isSnrActionOpen(data.status) ? SNR_POLL_MS : false
    },
    refetchIntervalInBackground: false,
  })
}

// ── Lists ───────────────────────────────────────────────────────────────────────

export interface SnrMessagesParams {
  page: number
  limit: number
  sortBy: string
  sortDir: 'asc' | 'desc'
  where?: ErSnrQuery
  filters?: SnrResultFilters
}

/** DECISIONS #6: the results open newest received first, ties broken by id on the server. */
export const SNR_DEFAULT_SORT = { id: 'receivedAt', desc: true } as const

function resultFilterParams(where: ErSnrQuery | undefined, filters: SnrResultFilters | undefined) {
  return {
    where: where ? JSON.stringify(where) : undefined,
    search: filters?.search,
    domainIds: filters?.domainIds?.join(','),
    providers: filters?.providers?.join(','),
    recipientIds: filters?.recipientIds?.join(','),
  }
}

export interface SnrPage<T> {
  rows: T[]
  meta: ErListMeta
}

/** A copy's link domains, defined (link intelligence): the API's grouping, else one entry per host. */
function withLinkDomains(row: SnrMessageRow): SnrMessageRow {
  const hosts = Array.isArray(row.linkHosts) ? row.linkHosts : []
  const linkDomains = Array.isArray(row.linkDomains)
    ? row.linkDomains
        .filter((d) => d && typeof d.domain === 'string' && d.domain)
        .map((d) => ({ domain: d.domain, hosts: Array.isArray(d.hosts) ? d.hosts : [d.domain] }))
    : hosts.map((h) => ({ domain: h, hosts: [h] }))
  return { ...row, linkHosts: hosts, linkDomains }
}

export function useSnrRunMessages(id: string | null, params: SnrMessagesParams, active: boolean) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: id ? snrV2Keys.messages(id, params) : [...snrV2Keys.all(), 'messages', 'idle'],
    queryFn: () =>
      erFetch<ErListEnvelope<SnrMessageRow>>(
        base(
          `${id}/messages${erQueryString({
            page: params.page,
            limit: params.limit,
            sortBy: params.sortBy,
            sortDir: params.sortDir,
            ...resultFilterParams(params.where, params.filters),
          })}`,
        ),
      ),
    enabled: scoped && !!id,
    placeholderData: keepPreviousData,
    select: (env): SnrPage<SnrMessageRow> => ({ rows: selectEnvelopeData(env).map(withLinkDomains), meta: selectEnvelopeMeta(env) }),
    // New matches keep arriving while the search is still working.
    refetchInterval: active ? SNR_POLL_MS : false,
    refetchIntervalInBackground: false,
  })
}

/**
 * Download the copies as CSV (GET /:id/messages/export): the same filters as the table the
 * analyst is looking at, so the file and the screen cannot disagree (contract "Changes", snr-web).
 */
export function useSnrExportResults(runId: string) {
  return useMutation({
    mutationFn: async (args: { where?: ErSnrQuery; filters?: SnrResultFilters }) => {
      const blob = await erDownload(base(`${runId}/messages/export${erQueryString(resultFilterParams(args.where, args.filters))}`))
      saveBlob(blob, `er-search-remediate-${runId}-messages.csv`)
    },
  })
}

export interface SnrMailboxesParams {
  page: number
  limit: number
  sortBy: string
  sortDir: 'asc' | 'desc'
  status?: SnrMailboxStatus
  search?: string
  /** The run's gaps list (owner, 3 Nov 2026): the mailboxes not searched that can be (Failed, or Skipped as not available). */
  notSearched?: boolean
}

export function useSnrRunMailboxes(id: string | null, params: SnrMailboxesParams, active: boolean) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: id ? snrV2Keys.mailboxes(id, params) : [...snrV2Keys.all(), 'mailboxes', 'idle'],
    queryFn: () => erFetch<ErListEnvelope<SnrMailboxRow>>(base(`${id}/mailboxes${erQueryString({ ...params })}`)),
    enabled: scoped && !!id,
    placeholderData: keepPreviousData,
    select: (env): SnrPage<SnrMailboxRow> => ({ rows: selectEnvelopeData(env), meta: selectEnvelopeMeta(env) }),
    refetchInterval: active ? SNR_POLL_MS : false,
    refetchIntervalInBackground: false,
  })
}

export function useSnrRunFacets(
  id: string | null,
  args: { where?: ErSnrQuery; filters?: SnrResultFilters },
) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: id ? snrV2Keys.facets(id, args) : [...snrV2Keys.all(), 'facets', 'idle'],
    queryFn: () => erFetch<SnrFacets>(base(`${id}/facets${erQueryString(resultFilterParams(args.where, args.filters))}`)),
    enabled: scoped && !!id,
    placeholderData: keepPreviousData,
  })
}

// ── Run controls (the existing routes, made durable) ────────────────────────────

function invalidateRun(qc: QueryClient, id: string) {
  void qc.invalidateQueries({ queryKey: snrV2Keys.progress(id) })
  void qc.invalidateQueries({ queryKey: snrV2Keys.jobsOf(id) })
  void qc.invalidateQueries({ queryKey: snrV2Keys.messagesOf(id) })
  void qc.invalidateQueries({ queryKey: snrV2Keys.mailboxesOf(id) })
  void qc.invalidateQueries({ queryKey: erKeys.searchRemediate.list() })
}

export type SnrRunControl = 'pause' | 'resume' | 'cancel'

export function useSnrRunControl(id: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (control: SnrRunControl) => erFetch<{ status: string }>(base(`${id}/${control}`), post()),
    onSuccess: () => {
      if (id) invalidateRun(qc, id)
    },
  })
}

/** "Searched with gaps": try the retryable failed mailboxes again (contract "Changes", snr-web item 5). */
export function useSnrRetryRun(id: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => erFetch<{ status: string }>(base(`${id}/retry-failed`), post()),
    onSuccess: () => {
      if (id) invalidateRun(qc, id)
    },
  })
}

// ── The run history (Logs) ──────────────────────────────────────────────────────

export interface SnrRunListParams {
  page: number
  limit: number
  sortBy: string
  sortDir: 'asc' | 'desc'
  /** The ER domain selector (er-frontend-15): the history follows it. */
  domainId?: string | null
}

/**
 * GET /: the run history, domain-aware. A row made before v2 carries its legacy `criteria`; a v2
 * row also carries `query`, `mode` and `queryVersion` (SnrRunListExtras).
 */
export function useSnrRunList(params: SnrRunListParams) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: erKeys.searchRemediate.list({ ...params }),
    queryFn: () =>
      erFetch<ErListEnvelope<SnrSearch & SnrRunListExtras>>(
        `${base()}${erQueryString({
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
          domainId: params.domainId ?? undefined,
        })}`,
      ),
    enabled: scoped,
    placeholderData: keepPreviousData,
    select: (env): SnrPage<SnrSearch & SnrRunListExtras> => ({ rows: selectEnvelopeData(env), meta: selectEnvelopeMeta(env) }),
  })
}

// ── The jobs of a run ───────────────────────────────────────────────────────────

/** How many jobs the page asks for (the API allows 200). A run has a handful; the page says when there are more. */
export const SNR_JOBS_LIMIT = 50

/**
 * GET /:id/jobs: the remediation jobs of a run (an action, and the retries and undos of it), newest first. This
 * replaces the browser-side memory of started jobs. It refreshes while any job is not over, so a retry or an undo
 * started elsewhere (the activity dock, another tab) appears here.
 */
/** A job row with the done summary's parts always present (an older API sends neither). */
export function normalizeSnrJobRow(raw: SnrJobRow): SnrJobRow {
  const by = raw.createdBy as Partial<NonNullable<SnrJobRow['createdBy']>> | null | undefined
  return {
    ...raw,
    mailboxes: typeof raw.mailboxes === 'number' ? raw.mailboxes : null,
    createdBy: by && typeof by.id === 'string' && typeof by.email === 'string' ? { id: by.id, name: by.name ?? '', email: by.email } : null,
  }
}

export function useSnrRunJobs(id: string | null) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: id ? snrV2Keys.jobsOf(id) : [...snrV2Keys.all(), 'jobs', 'idle'],
    queryFn: () => erFetch<ErListEnvelope<SnrJobRow>>(base(`${id}/jobs${erQueryString({ page: 1, limit: SNR_JOBS_LIMIT })}`)),
    enabled: scoped && !!id,
    select: (env): SnrPage<SnrJobRow> => ({ rows: selectEnvelopeData(env).map(normalizeSnrJobRow), meta: selectEnvelopeMeta(env) }),
    refetchInterval: (query) => {
      const data = query.state.data as ErListEnvelope<SnrJobRow> | undefined
      return data && selectEnvelopeData(data).some((j) => isSnrActionOpen(j.status)) ? SNR_POLL_MS : false
    },
    refetchIntervalInBackground: false,
  })
}

/**
 * A copy's headers (GET /:id/messages/:messageId/headers): the ones the search kept, else read from the mailbox now.
 * A refusal (403 other domain, 404 gone, 503 throttled with `retryAfterSec`) is not retried behind the analyst: the
 * drawer says why and offers Retry.
 */
export function useSnrMessageHeaders(runId: string | null, messageId: string | null, enabled: boolean) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: runId && messageId ? snrV2Keys.headers(runId, messageId) : [...snrV2Keys.all(), 'headers', 'idle'],
    queryFn: () => erFetch<SnrMessageHeaders>(base(`${runId}/messages/${messageId}/headers`)),
    enabled: scoped && enabled && !!runId && !!messageId,
    retry: false,
    staleTime: 60_000,
  })
}

// ── Remediation: preview, confirm, job, undo, retry ─────────────────────────────

export interface SnrActionRequest {
  action: SnrActionVerb
  selection: SnrSelection
  /** Move only: the folder (Microsoft) or label (Gmail) the copies go to, created if missing. */
  folderName?: string
}

export function useSnrActionPreview(runId: string) {
  return useMutation({
    mutationFn: (req: SnrActionRequest) => erFetch<SnrActionPreview>(base(`${runId}/actions/preview`), post(req)),
  })
}

export function useSnrStartAction(runId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: SnrActionRequest & { confirmToken: string; typedCount?: number; comment?: string }) =>
      erFetch<{ jobId: string }>(base(`${runId}/actions`), post(req)),
    onSuccess: () => {
      invalidateRun(qc, runId)
      notifyActivitiesChanged()
    },
    // A confirmation that was refused as used (er_snr_confirm_used) usually means another tab or analyst already
    // started the job: read the list again so it shows, whatever the refusal was.
    onError: () => void qc.invalidateQueries({ queryKey: snrV2Keys.jobsOf(runId) }),
  })
}

export function useSnrRetryFailed(runId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (jobId: string) => erFetch<{ jobId: string }>(base(`actions/${jobId}/retry-failed`), post()),
    onSuccess: () => {
      invalidateRun(qc, runId)
      notifyActivitiesChanged()
    },
  })
}

/** Pause, resume or cancel ONE remediation job (a run has its own: `useSnrRunControl`). Cancel leaves what moved moved. */
export function useSnrJobControl(runId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ jobId, control }: { jobId: string; control: SnrRunControl }) =>
      erFetch<{ status: SnrActionJobStatus }>(base(`actions/${jobId}/${control}`), post()),
    onSuccess: (_result, { jobId }) => {
      void qc.invalidateQueries({ queryKey: snrV2Keys.action(jobId) })
      if (runId) invalidateRun(qc, runId)
      else void qc.invalidateQueries({ queryKey: erKeys.searchRemediate.all() })
      notifyActivitiesChanged()
    },
  })
}

/**
 * Undo, step one: how many copies an undo of this job would put back (POST /actions/:jobId/undo/preview). Above 25 it
 * also answers the token the confirmed undo must carry. Nothing moves.
 */
export function useSnrUndoPreview() {
  return useMutation({
    mutationFn: (jobId: string) => erFetch<SnrUndoPreview>(base(`actions/${jobId}/undo/preview`), post()),
  })
}

/**
 * Undo, step two: put each copy back where it was. Above 25 copies the preview's token and the typed count ride in
 * the body; 25 or fewer send no body. The new job is a child of the one undone.
 */
export function useSnrUndo(runId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ jobId, confirmToken, typedCount }: { jobId: string; confirmToken?: string | null; typedCount?: number }) =>
      erFetch<{ jobId: string }>(
        base(`actions/${jobId}/undo`),
        post(confirmToken ? { confirmToken, ...(typedCount !== undefined ? { typedCount } : {}) } : undefined),
      ),
    onSuccess: () => {
      invalidateRun(qc, runId)
      notifyActivitiesChanged()
    },
    // Already undone, or the confirmation was used: the undo job exists, so read the list again to show it.
    onError: () => void qc.invalidateQueries({ queryKey: snrV2Keys.jobsOf(runId) }),
  })
}

/**
 * What a permanent Delete needs here (GET /settings/delete-gate): the viewer's delete:ErHardDelete, the workspace's
 * hard-delete switch, the 1,000-copy cap and which providers grant it. The Actions menus read it to say WHICH
 * permission is missing before anything is asked; the server still checks every one on preview and confirm.
 */
export function useSnrDeleteGate() {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: [...snrV2Keys.settings(), 'delete-gate'] as const,
    queryFn: () => erFetch<SnrDeleteGate>(base('settings/delete-gate')),
    enabled: scoped,
    staleTime: 60_000,
  })
}

/**
 * A run made before v2 is acted on through the old route: Quarantine, Junk, Inbox, Trash and Delete (Delete behind
 * its gate and the typed count). `Move` is not offered there: the old route has no folder name.
 */
export function useSnrLegacyRemediate(runId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      action: 'Quarantine' | 'Junk' | 'Inbox' | 'Trash' | 'Delete'
      selectionType: 'select_ids' | 'select_all_pages'
      selectedIds?: string[]
      assertedCount?: number
      comment?: string
    }) => erFetch<{ success: boolean }>(base(`${runId}/remediate`), post(body)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: erKeys.searchRemediate.all() })
    },
  })
}

// ── Suppressed mailboxes (never searched) ───────────────────────────────────────

export interface SnrSuppressedMailboxes {
  count: number
  data: Array<{ recipientId: string; email: string; domainId: string }>
}

/** GET /suppressed-mailboxes: the mailboxes Search & Remediate never searches (executives, legal hold). */
export function useSnrSuppressedMailboxes() {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: snrV2Keys.suppressed(),
    queryFn: () => erFetch<SnrSuppressedMailboxes>(base('suppressed-mailboxes')),
    enabled: scoped,
    staleTime: 60_000,
  })
}

/**
 * PUT /suppressed-mailboxes: REPLACES the list with these recipients (update:ErSearchRemediate). The server refuses an
 * added id that is not a live recipient of the workspace's remediation-capable domains (er_snr_recipients_foreign).
 */
export function useSetSnrSuppressedMailboxes() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { recipientIds: string[] }) => erFetch<SnrSuppressedMailboxes>(base('suppressed-mailboxes'), put(body)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: snrV2Keys.suppressed() }),
  })
}

// ── Saved searches ──────────────────────────────────────────────────────────────

export type SnrSavedScope = 'mine' | 'workspace'

export function useSnrSavedSearches(scope: SnrSavedScope) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: snrV2Keys.saved(scope),
    queryFn: () => erFetch<SnrSavedSearch[]>(base(`saved${erQueryString({ scope })}`)),
    enabled: scoped,
    select: (rows): SnrSavedSearch[] => (Array.isArray(rows) ? rows : []),
  })
}

function invalidateSaved(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: [...snrV2Keys.all(), 'saved'] })
}

export function useSaveSnrSearch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { name: string; query: ErSnrQuery; shared: boolean }) =>
      erFetch<SnrSavedSearch>(base('saved'), post(body)),
    onSuccess: () => invalidateSaved(qc),
  })
}

export function useUpdateSnrSearch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; query?: ErSnrQuery; shared?: boolean }) =>
      erFetch<SnrSavedSearch>(base(`saved/${id}`), patch(body)),
    onSuccess: () => invalidateSaved(qc),
  })
}

export function useDeleteSnrSearch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => erFetch<void>(base(`saved/${id}`), { method: 'DELETE' }),
    onSuccess: () => invalidateSaved(qc),
  })
}
